/**
 * Everything the portfolio assistant is allowed to say about Rakshan.
 * Plain data, no imports — keep it factual and in sync with the site sections.
 */
export const contactInfo = {
  phone: '+91 9632612163',
  email: 'rakshanshetty2003@gmail.com',
  linkedin: 'linkedin.com/in/rakshan-shetty-953864225',
  github: 'github.com/Rakshan001',
  portfolio: 'https://rakshan-shetty.netlify.app/',
  resume: '/resume/Rakshan_Shetty_Resume.pdf',
};

export const profile = {
  name: 'Rakshan Shetty',
  firstName: 'Rakshan',
  title: 'Senior Software Developer',
  company: 'Foxwel.ai',
  location: 'Bantwal, Karnataka, India',
  yearsExperience: '2',
  summary:
    'Full stack engineer with 2 years of production experience in React, Next.js, Node.js and Python, and hands-on work building LLM features with RAG, LangChain and the Gemini API. At Foxwel.ai he designs the architecture of a business operations platform and leads the team building it. Before that he led the build of HobbyistDecals.com, and he maintains two open-source developer tools: Baton, for running several AI coding agents on one codebase, and Elmory, evidence-checked memory for coding agents.',
  availability:
    'Open to senior full-stack and AI engineering roles, freelance builds and technical collaborations. Remote-first, with hybrid and relocation possible for the right opportunity.',
  workMode: 'Remote-first; currently in a hybrid role. Comfortable working across time zones.',
};

export const experience = [
  {
    role: 'Senior Software Developer',
    company: 'Foxwel.ai',
    website: 'https://www.foxwel.ai/',
    period: 'Mar 2026 – Present',
    location: 'Remote (Hybrid)',
    summary:
      "Designs the architecture of an all-in-one business platform for client companies, covering team chat, orders, the product catalog and inventory, and Google Drive-style file storage, and leads the team building it.",
    bullets: [
      'Leads the team from the data model and API contracts to each release',
      'Built the file storage module: folders, uploads, sharing and role-based permissions',
      'Integrated the Gemini API into the platform',
    ],
    tech: ['React', 'Next.js', 'TypeScript', 'Node.js', 'Gemini API', 'REST APIs', 'RBAC', 'Tailwind CSS'],
  },
  {
    role: 'Software Development Engineer (Product Owner)',
    company: 'Anave Enterprises LLP',
    website: 'https://www.anaventerprise.com/',
    period: 'May 2025 – Jul 2026',
    location: 'Bengaluru, India',
    summary:
      'Led architecture and design for HobbyistDecals.com, a MERN decal marketplace shipping to 42 countries, working with the team, and contributed to Skyskale.com.',
    bullets: [
      'Custom order intake, a design approval loop between customers and the studio, and customer messaging',
      'Secure REST APIs for order tracking, split payments, affiliate marketing and loyalty points, with payment gateway and Shiprocket',
      'RAG chatbot over the catalog and order history, built with LangChain and traced in LangSmith, so customers can ask about products and their orders',
      '3 production React and Next.js apps serving 500+ active users; Google Drive-style file management in the company CRM',
      'React Native apps including real-time chat and video calling',
      'Page load down from 5s to 0.5s by refactoring, and deploy time cut by 60% with GitHub Actions',
    ],
    products: [
      { name: 'HobbyistDecals.com', url: 'https://hobbyistdecals.com' },
      { name: 'Skyskale.com', url: 'https://skyskale.com' },
    ],
    tech: ['React', 'Next.js', 'Node.js', 'Express', 'MongoDB', 'React Native', 'LangChain', 'LangSmith', 'RAG', 'AWS', 'GitHub Actions'],
  },
  {
    role: 'Full Stack and AI/ML Intern',
    company: 'Accolade Tech Solutions',
    period: 'Jan 2025 – May 2025',
    location: 'Mangalore, India',
    summary: 'Deep learning research for computer vision plus full-stack and mobile client work.',
    bullets: [
      'ML models reaching 95% prediction accuracy on benchmark datasets',
      'Voice-powered voting system for visually impaired users',
    ],
    tech: ['Python', 'TensorFlow', 'PyTorch', 'OpenCV', 'Flask', 'MySQL', 'Flutter'],
  },
  {
    role: 'AI/ML Intern',
    company: 'Entertainment Technologists',
    period: 'Sep 2024 – Nov 2024',
    location: 'Remote',
    summary: 'Three-month remote internship integrating open-source models and redesigning web interfaces.',
    bullets: ['Improved model performance by 70% with advanced open-source models', 'Multilingual result downloads'],
    tech: ['Python', 'Flask', 'JavaScript', 'Open-source models'],
  },
];

export const leadership = {
  role: 'Full Stack Developer & Lead',
  org: 'College Alumni Club',
  period: 'Nov 2024 – Feb 2025',
  summary: 'Led the build of the college alumni portal (Django, MySQL); cut latency 50% with caching and query optimisation; 200+ users.',
};

export const education = [
  { degree: 'B.E. Computer Science & Engineering (AI & ML)', institution: 'Canara Engineering College, Mangalore', period: '2021 – 2025', score: 'CGPA 8.5 / 10' },
  { degree: 'Higher Secondary (12th), Science – PCM', institution: 'S.V.S PU College, Bantwal', period: '2019 – 2021', score: '83.5%' },
  { degree: 'Secondary School (10th)', institution: 'Govt. High School, Bantwal', period: '2018 – 2019', score: '87.2%' },
];

export const spokenLanguages = ['Tulu (native)', 'English (full professional)', 'Kannada (full professional)', 'Hindi (full professional)'];

export const certifications = ['Python Programming', 'Full Stack Web Development', 'Machine Learning', 'Robotic Process Automation (RPA)', 'IoT Workshop'];

export const achievements = [
  'Best final year project award for the Blind Voting System (3 languages, 96% recognition accuracy)',
  'Ideathon competition winner (innovative education-system concept)',
  'Led architecture and design for HobbyistDecals.com, a decal marketplace shipping to 42 countries',
  'Brought page load at Anave down from 5s to 0.5s',
  'Baton: open-source orchestration for AI coding agents, running 10 to 15 agents on one codebase, 641 tests',
  'Elmory: open-source, evidence-checked memory for AI coding agents with a CLI and a nine-tool MCP server',
  '95% ML model accuracy during the Accolade Tech internship',
];

export const skills = {
  Languages: ['TypeScript', 'JavaScript', 'Python', 'SQL', 'PHP', 'Java'],
  Frontend: ['React', 'Next.js', 'React Native', 'Redux', 'Tailwind CSS', 'Vite', 'Framer Motion', 'Server-side rendering'],
  Backend: ['Node.js', 'Express', 'Django', 'Flask', 'REST APIs', 'Server-Sent Events', 'JWT auth', 'RBAC', 'Microservices'],
  'GenAI & LLMs': ['RAG', 'LangChain', 'LangGraph', 'LangSmith', 'Gemini API', 'OpenAI API', 'AI agents', 'Agent orchestration', 'Multi-agent systems', 'Prompt engineering', 'Embeddings', 'Vector search', 'MCP'],
  'AI / ML': ['TensorFlow', 'Keras', 'CNNs', 'Scikit-learn', 'OpenCV', 'NLP'],
  Databases: ['MongoDB', 'PostgreSQL', 'MySQL', 'SQLite', 'Redis'],
  'DevOps & Cloud': ['AWS (EC2, S3, SES)', 'Docker', 'GitHub Actions', 'CI/CD', 'Vercel', 'Netlify'],
  Tools: ['Git', 'Vitest', 'Agile'],
};

export const baton = {
  name: 'Baton',
  oneLiner:
    'An open-source (MIT) orchestration layer for AI coding agents. Claude Code plans and reviews, while Cursor, Codex, Gemini and free models build in parallel, all sharing one memory of the project.',
  story:
    'He built it at a startup where two testers filed bugs all day while product work still had to ship. A bug-fix skill he wrote reached a 95%+ success rate, and Baton grew from the question of running five or ten agents like that at once, each on the model that suits the task, instead of leaving paid and free AI accounts unused.',
  features: [
    'Plan on a strong model, build on cheaper ones: Claude Code plans and reviews, Cursor, Codex, Gemini and free models write the code',
    '10 to 15 agents on one codebase at once without overwriting each other, with automatic pause and resume when two need the same file',
    'One shared knowledge base, so any agent, even one installed today, already knows the project',
    'Token-efficient mode: an agent understands a codebase in about 1,000 tokens instead of millions',
    'Team mode connecting several developers and their agents, with notifications before anyone touches your work',
    'One-click project setup that installs and shares skills and rules, replacing 30 to 40 minutes per project',
    'Long-term project memory and token tracking per project',
  ],
  results:
    'He ran three projects in parallel for a week on one setup, with Claude Code spending its tokens on planning and review and cheaper agents doing most of the writing.',
  scale: 'About 16,000 lines of TypeScript and 641 automated tests. Ships as a desktop app, a CLI, an MCP server and a dashboard.',
  links: [
    { label: 'Baton live site', url: 'https://baton-landing.vercel.app' },
    { label: 'Baton on GitHub', url: 'https://github.com/Rakshan001/Baton-Multi-Agent-' },
    { label: 'Baton case study', url: '/project/12/baton' },
  ],
};

export const elmory = {
  name: 'Elmory',
  oneLiner:
    "Open-source (MIT) memory for AI coding agents. Every fact quotes the exact code that proves it, and Elmory serves the fact only while that quote is still in the code.",
  problem:
    'Agent memory goes stale silently. A note says sessions expire after 15 minutes, a teammate changes it to 30, and the next agent still acts on 15.',
  how: [
    'A fact must quote text copied exactly from git-tracked files; the quote has to be found exactly once, or the write is refused',
    'Every recall re-checks the quotes against the asking worktree: holds → served; quoted line changed → suspect; file deleted → stale',
    "If nothing holds, recall answers \"I don't know\" instead of guessing",
    'Decisions and preferences with no code to quote are stored as notes, labelled "not code-checked"',
    'Newer records replace older ones only through explicit supersedes links, never by timestamp',
    "Records from other people aren't served until a human approves them, and forget really deletes",
  ],
  interfaces:
    'A CLI (elmory remember, note, recall, verify, forget and more) and a stdio MCP server with nine tools, so Claude Code, Cursor, Codex or any MCP client can use it.',
  tech:
    'TypeScript on Node 24+ with git 2.31+. Memory lives in the repo as small immutable Markdown records under .elmory/, with a SQLite full-text cache (keyword search, no embeddings, runs offline). The core has no runtime dependencies beyond Node built-ins.',
  status: 'Pre-release: release 1 core is complete but not on npm yet, so it installs from source.',
  links: [
    { label: 'Elmory website', url: 'https://elmory.vercel.app' },
    { label: 'Elmory on GitHub', url: 'https://github.com/Rakshan001/elmory' },
    { label: 'Elmory case study', url: '/project/13/elmory' },
  ],
};
